import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDynamicConfig, useStatsigClient } from '@statsig/react-bindings';
import { useLocation } from 'react-router-dom';
import { buildAssistantHeaders, buildAssistantUrl } from "../../utils/assistantApi";
import { useAssistantAbly } from "../../contexts/AIAssistantAblyContext";
import {
    addMessage,
    appendToLastMessage,
    userTyping,
    assistantResponding,
    addSelectedValue,
    removeSelectedValue,
    removeAllSelectedValues,
    setActiveReport,
    addErrorMessage,
    clearErrorMessage,
    setTaskStatusMessage,
    setHistoryMetadata,
    setLastMessageTraceId,
    updateReportMessage,
    setFollowupSuggestions,
    clearFollowupSuggestions,
} from "../../state/actions";
import { AI_ASSISTANT_CONSTANTS } from "../../constants/aiAssistantConstants";
import { getLogoUrlFromArticle } from "../../utils/logoDev";
import ErrorCodes from '@/constants/errors/ErrorCodes';
import HistoryMetaData from "../HistoryMetaData";
import Tooltip from "../Tooltip";
import {
    FiArrowUp,
    FiFileText,
    FiBarChart2,
    FiPlus,
    FiSliders,
    FiMic,
    FiRss,
} from "react-icons/fi";
import { useScreenContext } from "../../hooks/useScreenContext";
import "./MessageInput.scss";

const NEWS_SEARCH_VALUE = AI_ASSISTANT_CONSTANTS.SELECTED_VALUES.NEWS_SEARCH;
const MessageInput = () => {
    const navigate = useNavigate();
    const { checkGate } = useStatsigClient();
    const config = useDynamicConfig(AI_ASSISTANT_CONSTANTS.DYNAMIC_CONFIGS.AI_ASSISTANT_CONFIGS);
    const location = useLocation();
    const [messageInput, setMessageInput] = useState("");
    const [isNewsSearchEnabled, setIsNewsSearchEnabled] = useState(false);
    const dispatch = useDispatch();
    const typingTimeoutRef = useRef(null);
    const inputRef = useRef(null);
    const [isInputFocused, setIsInputFocused] = useState(false);
    const { selectedValues, historyMetadata, isAssistantResponding } = useSelector((state) => state.chat);
    const userDetail = useSelector((state) => state.userDetail);
    const { clientId, conversationId, isSessionLoading } = useAssistantAbly();
    const isSummaryButtonEnabled = checkGate(AI_ASSISTANT_CONSTANTS.FEATURE_FLAGS.AI_ASSISTANT_SUMMARISE_SCREEN_ENABLED);
    const isReportBuilderButtonEnabled = checkGate(AI_ASSISTANT_CONSTANTS.FEATURE_FLAGS.AI_ASSISTANT_REPORT_BUILDER_ENABLED);
    const isHistoryMetadataContextEnabled = checkGate(AI_ASSISTANT_CONSTANTS.FEATURE_FLAGS.AI_ASSISTANT_HISTORY_METADATA_CONTEXT_ENABLED)
    const summaryHiddenRoutes = ["/visibility", "/reputation", "/reports"];
    const shouldHideSummary = summaryHiddenRoutes.some((route) =>
        location?.pathname?.startsWith(route)
    );
    const shouldShowButton = isSummaryButtonEnabled && !shouldHideSummary;
    const shouldShowReportBuilderButton = isReportBuilderButtonEnabled;
    const disableNewsSearchButton = false;
    const disableSummaryButton = false;
    const disableReportBuilderButton = true;
    const isNewsSearchActive = selectedValues.includes(NEWS_SEARCH_VALUE);
    const [toolsDropdownOpen, setToolsDropdownOpen] = useState(false);
    const toolsDropdownRef = useRef(null);
    const messageSelectedValues = selectedValues.filter(
        (value) => value !== NEWS_SEARCH_VALUE
    );
    const isSendDisabled =
        isAssistantResponding ||
        isSessionLoading ||
        !conversationId ||
        (messageInput.trim().length === 0 && messageSelectedValues.length === 0);
    const { buildCurrentScreenContext } = useScreenContext();
    const convertQueryParamsToObject = (searchString = window.location.search) => {
        const urlParams = new URLSearchParams(searchString);
        const paramsWithArrays = {};
        for (const [key, value] of urlParams) {
            if (paramsWithArrays[key]) {

                paramsWithArrays[key] = Array.isArray(paramsWithArrays[key])
                    ? [...paramsWithArrays[key], value]
                    : [paramsWithArrays[key], value];
            } else {
                paramsWithArrays[key] = value;
            }
        }
        return paramsWithArrays;
    };
    const createInitialReportState = (reportId) => ({
        report_id: reportId,
        title: "Building your report",
        description: "Gathering the freshest insights from this view.",
        sections: [],
        plan: null,
        status_message: "Initializing workspace...",
        build_progress: {
            currentStage: "initializing",
            startedAt: new Date().toISOString(),
            log: [],
        },
    });
    const toPlaceholderSections = (sections = []) =>
        sections
            .filter((section) => typeof section === "object" && section !== null)
            .map((section, idx) => ({
                section_id: `planned-${idx + 1}`,
                title: section.title || `Section ${idx + 1}`,
                objective: section.objective,
                focus_metrics: section.focus_metrics || [],
                visuals: section.visuals || [],
                is_placeholder: true,
                placeholder_status: "pending",
                plan_index: idx + 1,
                blocks: [],
            }));
    useEffect(() => {
        if (messageInput.length > 0 || isInputFocused) {
            dispatch(userTyping(true));
            clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = setTimeout(() => {
                dispatch(userTyping(false));
            }, 3000);
        } else {
            dispatch(userTyping(false));
        }
        return () => clearTimeout(typingTimeoutRef.current);
    }, [messageInput, isInputFocused, dispatch]);
    useEffect(() => {
        const textarea = inputRef.current;
        if (!textarea) return;
        const resizeObserver = new ResizeObserver(() => {
            adjustTextareaHeight();
        });
        resizeObserver.observe(textarea);
        return () => resizeObserver.disconnect();
    }, []);

    useEffect(() => {
        if (!toolsDropdownOpen) return;
        const handleClickOutside = (event) => {
            if (
                toolsDropdownRef.current &&
                !toolsDropdownRef.current.contains(event.target)
            ) {
                setToolsDropdownOpen(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === "Escape") setToolsDropdownOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [toolsDropdownOpen]);
    const resetTextareaHeight = () => {
        if (inputRef.current) {
            inputRef.current.style.height = "auto";
            inputRef.current.style.overflowY = "hidden";
        }
    };
    const typingQueueRef = useRef([]);
    const isTypingAnimationRef = useRef(false);
    const TYPE_CHUNK_SIZE = 6;
    const TYPE_CHUNK_DELAY = 25;

    const scrollMessagesToBottom = () => {
        const container = document.querySelector(".msg-container");
        if (container) {
            container.scrollTop = container.scrollHeight;
        }
    };

    const enqueueTextAppend = async (text) => {
        if (!text) return;
        typingQueueRef.current.push(text);
        if (isTypingAnimationRef.current) return;
        isTypingAnimationRef.current = true;

        const typeOut = (payload) =>
            new Promise((resolve) => {
                if (!payload) return resolve();
                let idx = 0;
                const step = () => {
                    if (idx >= payload.length) return resolve();
                    const nextSlice = payload.slice(idx, idx + TYPE_CHUNK_SIZE);
                    dispatch(appendToLastMessage(nextSlice));
                    scrollMessagesToBottom();
                    idx += TYPE_CHUNK_SIZE;
                    setTimeout(step, TYPE_CHUNK_DELAY);
                };
                step();
            });

        while (typingQueueRef.current.length) {
            const next = typingQueueRef.current.shift();

            await typeOut(next);
        }
        isTypingAnimationRef.current = false;
    };

    const handleStreamingResponse = async (responseStream) => {
        const normalizeChunk = (chunkJson = {}) => {
            const basePayload = { ...(chunkJson.payload || {}) };
            const pick = (...keys) => {
                for (const key of keys) {
                    if (basePayload[key] !== undefined) return basePayload[key];
                    if (chunkJson[key] !== undefined) return chunkJson[key];
                }
                return undefined;
            };
            // Protocol: sources event sends data: { articles, source_type, grounding_sources }
            const data = chunkJson.data ?? chunkJson.payload?.data;
            const articles = data?.articles ?? pick("sources") ?? [];
            const source_type = data?.source_type ?? chunkJson.source_type ?? pick("source_type");
            const grounding_sources = data?.grounding_sources ?? chunkJson.grounding_sources ?? pick("grounding_sources");

            return {
                type: chunkJson.type,
                text: pick("text", "data"),
                taskTitle: pick("taskTitle", "title"),
                taskMessage: pick("taskMessage", "message"),
                assumption: pick("assumption"),
                sources: Array.isArray(articles) ? articles : pick("sources"),
                source_type,
                grounding_sources,
                chartConfig: pick("chartConfig"),
                tableConfig: pick("tableConfig"),
                metadata: pick("metadata"),
                link: pick("link", "url"),
                url: pick("url", "link"),
                title: pick("title"),
                description: pick("description"),
                stage: pick("stage"),
                suggestions: Array.isArray(chunkJson.suggestions)
                    ? chunkJson.suggestions
                    : Array.isArray(basePayload.suggestions)
                        ? basePayload.suggestions
                        : Array.isArray(data?.suggestions)
                            ? data.suggestions
                            : undefined,
                raw: chunkJson,
            };
        };

        // Track whether we've added the Harris text placeholder yet.
        // We delay this until the first real text chunk to avoid an empty bubble.
        let hasAddedPlaceholder = false;
        const ensurePlaceholder = () => {
            if (!hasAddedPlaceholder) {
                dispatch(addMessage({ type: "Harris", message_type: "text", payload: { text: "" } }));
                hasAddedPlaceholder = true;
            }
        };

        const processChunk = async (chunkJson) => {
            const chunk = normalizeChunk(chunkJson);
            const chunkType = chunk.type;
            if (!chunkType) return;

            switch (chunkType) {
                case "assumption":
                    dispatch(addMessage({
                        type: "Harris",
                        message_type: "assumption",
                        payload: { assumption: chunk.assumption || chunk.text || "" }
                    }));
                    dispatch(addMessage({ type: "Harris", message_type: "text", payload: { text: "" } }));
                    hasAddedPlaceholder = true;
                    break;
                case "sources": {
                    const articles = Array.isArray(chunk.sources) ? chunk.sources : [];
                    const logoToken = AI_ASSISTANT_CONSTANTS.LOGO_DEV_PUBLISHABLE_KEY || "";
                    const enriched = articles.map((a) => ({
                        ...a,
                        icon: a.icon || getLogoUrlFromArticle(a, logoToken, 32),
                    }));
                    dispatch(addMessage({
                        type: "Harris",
                        message_type: "sources",
                        payload: {
                            sources: enriched,
                            source_type: chunk.source_type,
                            grounding_sources: chunk.grounding_sources,
                        },
                    }));
                    break;
                }
                case "suggestions": {
                    const suggestions = Array.isArray(chunk.suggestions) ? chunk.suggestions : [];
                    if (suggestions.length > 0) {
                        dispatch(setFollowupSuggestions(suggestions));
                    }
                    break;
                }
                case "chart":
                    dispatch(addMessage({
                        type: "Harris",
                        message_type: "chart",
                        payload: { chartConfig: chunk.chartConfig || {} }
                    }));
                    break;
                case "table":
                    dispatch(addMessage({
                        type: "Harris",
                        message_type: "table",
                        payload: { tableConfig: chunk.tableConfig || {} }
                    }));
                    break;
                case "status":
                case "task_status":
                    dispatch(setTaskStatusMessage({
                        type: "task_status",
                        taskTitle: chunk.raw?.message || chunk.taskMessage || chunk.taskTitle || "Working on it...",
                        taskMessage: chunk.raw?.message || chunk.taskMessage || "",
                    }));
                    break;
                case "history_metadata":
                    if (chunk.metadata !== undefined) {
                        dispatch(setHistoryMetadata(chunk.metadata));
                    }
                    break;
                case "text":
                case "data":
                    if (chunk.text !== undefined) {
                        ensurePlaceholder();
                        await enqueueTextAppend(chunk.text);
                    }
                    break;
                case "link":
                    dispatch(addMessage({
                        type: "Harris",
                        message_type: "link",
                        payload: {
                            title: chunk.title || "Link",
                            link: chunk.link,
                            description: chunk.description || chunk.text || "",
                        }
                    }));
                    break;
                case "navigate": {
                    const raw = chunk.raw || {};
                    const navLink =
                        raw.url ??
                        raw.link ??
                        raw.payload?.url ??
                        raw.payload?.link ??
                        raw.data?.url ??
                        raw.data?.link ??
                        chunk.link ??
                        chunk.url;
                    try {
                        if (navLink && typeof navLink === "string") {
                            if (navLink.startsWith("http")) {
                                const url = new URL(navLink);
                                const isSameOrigin = url.origin === window.location.origin;
                                if (isSameOrigin) {
                                    navigate(url.pathname + url.search);
                                } else {
                                    window.location.href = navLink;
                                }
                            } else {
                                navigate(navLink);
                            }
                        }
                    } catch (error) {
                        if (navLink && typeof navLink === "string" && !navLink.startsWith("http")) {
                            navigate(navLink);
                        }
                    }
                    break;
                }
                case "error":
                    dispatch(addErrorMessage(`\n${chunk.raw?.message || chunk.text || chunk.taskMessage || "Unexpected error"}`));
                    break;
                case "custom_type":
                case "custom":
                    const customData = chunk.raw?.data ?? chunk.raw;
                    dispatch(addMessage({
                        type: "Harris",
                        message_type: "custom",
                        payload: {
                            data: customData,
                            text: typeof customData === "string" ? customData : JSON.stringify(customData),
                        }
                    }));
                    break;
                case "end": {
                    const meta = chunk.metadata ?? chunk.raw?.metadata;
                    const traceId =
                        (typeof meta?.trace_id === "string" && meta.trace_id) ||
                        (typeof meta?.traceId === "string" && meta.traceId) ||
                        null;
                    if (traceId) {
                        dispatch(setLastMessageTraceId(traceId));
                    }
                    dispatch(userTyping(false));
                    dispatch(setTaskStatusMessage({ type: "task_status_end" }));
                    break;
                }
                default:
                    if (chunk.text) {
                        ensurePlaceholder();
                        dispatch(appendToLastMessage(chunk.text));
                    }
                    break;
            }
        };

        try {
            const reader = responseStream.getReader();
            const decoder = new TextDecoder("utf-8");
            let buffer = "";
            dispatch(setHistoryMetadata(null));
            dispatch(setTaskStatusMessage(null));
            // Note: We no longer add the empty Harris message here.
            // ensurePlaceholder() below will add it on the first real text chunk.
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const parts = buffer.split("\n");
                buffer = parts.pop();
                for (const part of parts) {
                    let trimmed = part.trim();
                    if (!trimmed) continue;
                    // SSE lines arrive as "data: {...}" — strip the prefix
                    if (trimmed.startsWith("data: ")) trimmed = trimmed.slice(6);
                    else if (trimmed.startsWith("data:")) trimmed = trimmed.slice(5);
                    // Skip SSE comment/control lines (e.g. ":ping", "event:", "id:", "retry:")
                    if (!trimmed || trimmed.startsWith(":") || trimmed.startsWith("event:") || trimmed.startsWith("id:") || trimmed.startsWith("retry:")) continue;
                    try {
                        const chunkJson = JSON.parse(trimmed);
                        await processChunk(chunkJson);
                        if (chunkJson.type === "end") {
                            return;
                        }
                    } catch {
                        console.warn("Failed to parse chunk JSON:", trimmed);
                    }
                }
            }
            if (buffer.trim()) {
                let leftover = buffer.trim();
                if (leftover.startsWith("data: ")) leftover = leftover.slice(6);
                else if (leftover.startsWith("data:")) leftover = leftover.slice(5);
                if (leftover && !leftover.startsWith(":")) {
                    try {
                        const chunkJson = JSON.parse(leftover);
                        await processChunk(chunkJson);
                    } catch (error) {
                        console.warn("Error parsing leftover buffer:", error?.message);
                    }
                }
            }
            dispatch(assistantResponding(false));
            dispatch(userTyping(false));

        } catch (error) {
            console.error("Streaming error:", error);
            const uiError = ErrorCodes["UI-E-001"]
            const code = uiError?.code ?? "UI-E-001";
            const message = error?.message ?? "Unexpected UI Error. Please contact the admin.";
            dispatch(addErrorMessage(`\n[${code}] - ${message}`));
        } finally {
            dispatch(assistantResponding(false));
            dispatch(userTyping(false));

        }
    };
    const streamReportBuild = async (clientReportId, requestPayload, initialReportState) => {
        let reportSnapshot = { ...initialReportState };
        let statusMessage = "Initializing workspace...";
        let buildCompleted = false;

        const pushUpdate = (overrides = {}) => {
            dispatch(updateReportMessage(clientReportId, {
                report_data: { ...reportSnapshot },
                report_status: overrides.report_status ?? "processing",
                report_status_message: overrides.report_status_message ?? statusMessage,
                report_title: reportSnapshot.title,
            }));
        };

        const response = await fetch(
            buildAssistantUrl("build-report/"),
            {
                method: "POST",
                headers: buildAssistantHeaders(),
                credentials: "include",
                body: JSON.stringify({ ...requestPayload, clientReportId }),
            }
        );

        if (!response.ok) {
            const err = new Error(response.statusText || "Server error");
            err.origin = "backend";
            throw err;
        }

        if (!response.body) {
            throw new Error("ReadableStream not supported");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";
        pushUpdate({ report_status_message: statusMessage });

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split("\n");
            buffer = parts.pop();
            for (const part of parts) {
                const trimmed = part.trim();
                if (!trimmed) {
                    continue;
                }
                let chunkJson;
                try {
                    chunkJson = JSON.parse(trimmed);
                } catch (error) {
                    console.warn("Invalid JSON chunk from report stream", error);
                    continue;
                }
                if (chunkJson.client_report_id && chunkJson.client_report_id !== clientReportId) {
                    continue;
                }
                const { type } = chunkJson;
                if (type === "report_status") {
                    const stage = chunkJson.stage || reportSnapshot.build_progress?.currentStage || "processing";
                    statusMessage = chunkJson.message || statusMessage;
                    const logEntry = {
                        stage,
                        message: statusMessage,
                        timestamp: new Date().toISOString(),
                        detail: chunkJson.detail,
                    };
                    const prevLog = reportSnapshot.build_progress?.log || [];
                    const nextLog = [...prevLog, logEntry].slice(-25);
                    reportSnapshot = {
                        ...reportSnapshot,
                        status_message: statusMessage,
                        build_progress: {
                            ...(reportSnapshot.build_progress || {}),
                            currentStage: stage,
                            lastUpdate: new Date().toISOString(),
                            log: nextLog,
                            totalSections: chunkJson.total_sections ?? reportSnapshot.build_progress?.totalSections,
                            completedSections: chunkJson.completed_sections ?? reportSnapshot.build_progress?.completedSections,
                        },
                    };
                    pushUpdate({ report_status_message: statusMessage });
                } else if (type === "report_plan") {
                    const planSections = toPlaceholderSections(chunkJson.sections || []);
                    reportSnapshot = {
                        ...reportSnapshot,
                        title: chunkJson.title || reportSnapshot.title,
                        description: chunkJson.description || reportSnapshot.description,
                        plan: {
                            title: chunkJson.title,
                            description: chunkJson.description,
                            sections: chunkJson.sections || [],
                        },
                        sections: planSections,
                        status_message: statusMessage,
                        build_progress: {
                            ...(reportSnapshot.build_progress || {}),
                            totalSections: planSections.length || reportSnapshot.build_progress?.totalSections,
                        },
                    };
                    pushUpdate();
                } else if (type === "report_section") {
                    const sectionData = chunkJson.section;
                    if (!sectionData) {
                        continue;
                    }
                    const targetIndex = Math.max((chunkJson.index || 1) - 1, 0);
                    const existingSections = reportSnapshot.sections ? [...reportSnapshot.sections] : [];
                    if (targetIndex >= existingSections.length) {
                        existingSections[targetIndex] = { ...sectionData, is_placeholder: false };
                    } else {
                        existingSections[targetIndex] = { ...sectionData, is_placeholder: false };
                    }
                    const completed = chunkJson.completed_sections ?? reportSnapshot.build_progress?.completedSections ?? targetIndex + 1;
                    const total = chunkJson.total_sections ?? reportSnapshot.build_progress?.totalSections ?? existingSections.length;
                    statusMessage = `Section ${completed}/${total} finalized`;
                    reportSnapshot = {
                        ...reportSnapshot,
                        sections: existingSections,
                        status_message: statusMessage,
                        build_progress: {
                            ...(reportSnapshot.build_progress || {}),
                            completedSections: completed,
                            totalSections: total,
                            currentStage: "section_ready",
                        },
                    };
                    pushUpdate({ report_status_message: statusMessage });
                } else if (type === "report_complete") {
                    const finalReport = chunkJson.report || {};
                    const finalReportId = finalReport.report_id || clientReportId;
                    const mergedReport = {
                        ...finalReport,
                        plan: reportSnapshot.plan,
                        status_message: "Report ready",
                        build_progress: {
                            ...(reportSnapshot.build_progress || {}),
                            currentStage: "completed",
                            completedAt: new Date().toISOString(),
                            log: [
                                ...(reportSnapshot.build_progress?.log || []),
                                {
                                    stage: "completed",
                                    message: "Report ready",
                                    timestamp: new Date().toISOString(),
                                },
                            ].slice(-25),
                        },
                    };
                    reportSnapshot = mergedReport;
                    dispatch(updateReportMessage(clientReportId, {
                        report_id: finalReportId,
                        report_status: "success",
                        report_status_message: "Report ready",
                        report_title: mergedReport.title || "AI Report",
                        report_data: mergedReport,
                    }));
                    dispatch(setActiveReport(finalReportId));
                    buildCompleted = true;
                    return finalReportId;
                } else if (type === "report_error") {
                    const errorMessage = chunkJson?.error?.message || chunkJson?.message || "Report build failed.";
                    reportSnapshot = {
                        ...reportSnapshot,
                        error: chunkJson.error || { message: errorMessage },
                        status_message: errorMessage,
                        build_progress: {
                            ...(reportSnapshot.build_progress || {}),
                            currentStage: "error",
                            log: [
                                ...(reportSnapshot.build_progress?.log || []),
                                {
                                    stage: "error",
                                    message: errorMessage,
                                    timestamp: new Date().toISOString(),
                                },
                            ].slice(-25),
                        },
                    };
                    dispatch(updateReportMessage(clientReportId, {
                        report_status: "failure",
                        report_status_message: errorMessage,
                        report_title: reportSnapshot.title,
                        report_data: reportSnapshot,
                    }));
                    buildCompleted = true;
                    return null;
                }
            }
        }

        if (!buildCompleted) {
            throw new Error("Report stream ended unexpectedly.");
        }

        return null;
    };
    const handleBuildReportClick = async () => {
        const report_id = `report-${Date.now()}`;
        const initialState = createInitialReportState(report_id);
        const initialReportMessage = {
            type: "Harris",
            message_type: "report",
            report_id: report_id,
            report_data: initialState,
            report_status: "processing",
            report_status_message: "Initializing workspace...",
            report_title: "Building the report",
            payload: {
                report_id: report_id,
                report_data: initialState,
                report_status: "processing",
                report_status_message: "Initializing workspace...",
                report_title: "Building the report"
            }
        };
        dispatch(addMessage(initialReportMessage));
        dispatch(setActiveReport(report_id));

        try {
            const data = convertQueryParamsToObject(location?.search || "");
            const page = location.pathname;
            const payload = { data, page };
            await streamReportBuild(report_id, payload, initialState);
        } catch (error) {
            console.error("Build report error:", error);
            const updatedData = {
                report_status: "failure",
                report_status_message: error?.message || "Failed to build report",
                report_title: "Failed to build report",
                report_data: {
                    ...initialState,
                    status_message: error?.message || "Failed to build report",
                    error: { message: error?.message || "Failed to build report" },
                },
            };
            dispatch(updateReportMessage(report_id, updatedData));
        }
    };

    React.useEffect(() => {
        const handleSuggestionSend = (event) => {
            const text = event?.detail?.text;
            if (!text || isAssistantResponding) return;
            performSend(text, { clearInput: true });
        };

        window.addEventListener("ai-assistant-send-suggestion", handleSuggestionSend);
        return () => {
            window.removeEventListener("ai-assistant-send-suggestion", handleSuggestionSend);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAssistantResponding]);
    const performSend = async (combinedMessage, { clearInput = true } = {}) => {
        try {
            if (!combinedMessage.trim()) return;
            dispatch(addMessage({
                type: "You",
                message_type: "text",
                text: combinedMessage,
                payload: { text: combinedMessage }
            }));
            messageSelectedValues.forEach((value) => dispatch(removeSelectedValue(value)));
            if (clearInput) {
                setMessageInput("");
                resetTextareaHeight();
                setIsInputFocused(false);
                if (inputRef.current) {
                    inputRef.current.blur();
                }
            }
            dispatch(assistantResponding(true));
            dispatch(clearErrorMessage());
            dispatch(clearFollowupSuggestions());
            const response = await getData(combinedMessage);
            if (!response.body) {
                const err = new Error("ReadableStream not supported");
                err.origin = 'ui';
                throw err;
            }
            await handleStreamingResponse(response.body);
        } catch (err) {
            console.error("API Error:", err);
            let code, message;
            if (err.origin === 'backend') {
                const backendError = ErrorCodes["SERVER-E-001"];
                code = backendError?.code ?? "SERVER-E-001";
                message = err?.message ?? "Internal Server error";
            } else {
                const uiError = ErrorCodes["UI-E-001"];
                code = uiError?.code ?? "UI-E-001";
                message = err?.message ?? "Unexpected UI Error. Please contact the admin.";
            }
            dispatch(addErrorMessage(`\n[${code}] - ${message}`));
            dispatch(assistantResponding(false));
        }
    };

    const submitMessageButtonClickHandler = async () => {
        if (!messageInput.trim() && messageSelectedValues.length === 0) return;
        const selectedValuesString = messageSelectedValues.join(", ");
        const combinedMessage =
            selectedValuesString.length > 0
                ? `${messageInput} [${selectedValuesString}]`
                : messageInput;
        await performSend(combinedMessage, { clearInput: true });
    };
    const getData = async (userInputMessage) => {
        try {
            const page = location.pathname;
            const currentScreenContext = buildCurrentScreenContext();
            const payload = {
                page,
                userInputMessage,
                clientId,
                conversationId,
                currentScreenContext,
                flags: {
                    includeExternalNews: isNewsSearchActive,
                },
            };
            const response = await fetch(
                buildAssistantUrl("usermessage"),
                {
                    method: "POST",
                    headers: buildAssistantHeaders({
                        "Client-Id": clientId,
                    }),
                    credentials: "include",
                    body: JSON.stringify(payload),
                }
            );
            if (!response.ok) {
                const err = new Error(response.statusText || 'Server error');
                err.origin = 'backend';
                err.status = response.status;
                throw err;
            }
            return response
        } catch (err) {
            err.origin ??= 'ui';
            throw err;
        }
    }
    const messageChangeHandler = (e) => {
        setMessageInput(e.target.value);
        adjustTextareaHeight();
    };
    const handleNewsSearchToggle = (value) => {
        if (disableNewsSearchButton) return;
        setIsNewsSearchEnabled(value);
    };
    const handleFocus = () => setIsInputFocused(true);
    const handleKeyPress = (event) => {
        if (event.key === "Enter" && messageInput.trim() !== "" && !isAssistantResponding) {
            event.preventDefault();
            submitMessageButtonClickHandler();
        }
    };
    const toggleCheckbox = (value) => {
        if (selectedValues.includes(value)) {
            dispatch(removeSelectedValue(value));
        } else {
            dispatch(addSelectedValue(value));
        }
    };
    const toggleNewsSearch = () => toggleCheckbox(NEWS_SEARCH_VALUE);
    const adjustTextareaHeight = () => {
        const textarea = inputRef.current;
        if (!textarea) return;
        textarea.style.height = "auto";
        const maxHeight = 200;
        const newHeight = Math.min(textarea.scrollHeight, maxHeight);
        textarea.style.height = `${newHeight}px`;
        textarea.style.overflowY =
            textarea.scrollHeight > maxHeight ? "auto" : "hidden";
    };
    const getPageSummary = async () => {
        try {
            const userInputMessage = 'Summarize this screen'
            await performSend(userInputMessage, { clearInput: false });
        } catch (err) {
            let code, message;
            if (err.origin === 'backend') {
                const backendError = ErrorCodes["SERVER-E-001"]
                code = backendError?.code ?? "SERVER-E-001";
                message = err?.message ?? "Internal Server error";
            } else {
                const uiError = ErrorCodes["UI-E-001"]
                code = uiError?.code ?? "UI-E-001";
                message = err?.message ?? "Unexpected UI Error. Please contact the admin.";
            }
            dispatch(addErrorMessage(`\n[${code}] - ${message}`));
            dispatch(assistantResponding(false));

        }
    }
    return (
        <form className="card-actions" onSubmit={(e) => e.preventDefault()}>
            <div className="card-footer-wrapper">
                {isNewsSearchActive && (
                    <div className="news-included-pill" role="status">
                        <FiRss className="news-included-pill__icon" aria-hidden />
                        <span>News included in results</span>
                    </div>
                )}
                {isHistoryMetadataContextEnabled && isInputFocused && <HistoryMetaData />}
                <div className="chatbox-shell-wrapper">
                    {isAssistantResponding && <div className="chatbox-thinking-canvas" />}
                    <div className="card-footer-container chatbox-shell">
                        <div className="card-footer-input-container">
                            <textarea
                                className="textarea"
                                placeholder={AI_ASSISTANT_CONSTANTS.INPUT.PLACEHOLDER}
                                onKeyDown={handleKeyPress}
                                ref={inputRef}
                                onChange={messageChangeHandler}
                                value={messageInput}
                                onFocus={handleFocus}
                                rows={1}
                            />
                        </div>
                        <div className="chatbox-controls-row">
                            <div className="chatbox-left-controls" ref={toolsDropdownRef}>
                                <button
                                    type="button"
                                    className="utility-btn utility-btn--icon"
                                    aria-label="More tools coming soon"
                                    disabled
                                >
                                    <FiPlus />
                                </button>
                                <Tooltip content="Tools and options" placement="bottom">
                                    <span>
                                        <button
                                            type="button"
                                            className={`utility-btn utility-btn--pill ${isNewsSearchActive ? "is-active" : ""}`}
                                            disabled={disableNewsSearchButton}
                                            onClick={disableNewsSearchButton ? undefined : () => setToolsDropdownOpen((prev) => !prev)}
                                            aria-disabled={disableNewsSearchButton}
                                            aria-haspopup="true"
                                            aria-expanded={toolsDropdownOpen}
                                            aria-label="Toggle tools menu"
                                        >
                                            <FiSliders />
                                            <span>Tools</span>
                                        </button>
                                    </span>
                                </Tooltip>
                                {toolsDropdownOpen && (
                                    <div
                                        className="tools-dropdown"
                                        role="dialog"
                                        aria-label="Tools options"
                                    >
                                        <div className="tools-dropdown__header">Connected Tools</div>
                                        <div className="tools-dropdown__list">
                                            <button
                                                type="button"
                                                className="tool-menu-item"
                                                onClick={() => {
                                                    if (!disableNewsSearchButton) {
                                                        toggleCheckbox(NEWS_SEARCH_VALUE);
                                                    }
                                                }}
                                                disabled={disableNewsSearchButton}
                                            >
                                                <div className="tool-menu-item__icon">
                                                    <FiRss />
                                                </div>
                                                <div className="tool-menu-item__content">
                                                    <div className="tool-menu-item__title">External News</div>
                                                    <div className="tool-menu-item__desc">Include real-time news sources</div>
                                                </div>
                                                <div className={`tool-menu-toggle${isNewsSearchActive ? " is-on" : ""}`}>
                                                    <div className="tool-menu-toggle__thumb" />
                                                </div>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="chatbox-right-controls">
                                <button
                                    type="button"
                                    className="utility-btn utility-btn--icon"
                                    aria-label="Voice input coming soon"
                                    disabled
                                >
                                    <FiMic />
                                </button>
                                <button
                                    className="submit-btn"
                                    type="button"
                                    onClick={submitMessageButtonClickHandler}
                                    aria-label="Send message"
                                    disabled={isSendDisabled}
                                >
                                    <FiArrowUp />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="chatbox-chip-row">
                    {shouldShowButton && (
                        <button
                            type="button"
                            onClick={disableSummaryButton ? undefined : getPageSummary}
                            className="chatbox-chip"
                            aria-disabled={disableSummaryButton}
                            disabled={disableSummaryButton}
                        >
                            <FiFileText className="chip-icon" />
                            Summary
                        </button>
                    )}

                    {shouldShowReportBuilderButton && (
                        <button
                            type="button"
                            className="chatbox-chip"
                            onClick={disableReportBuilderButton ? undefined : handleBuildReportClick}
                            aria-disabled={disableReportBuilderButton}
                            disabled={disableReportBuilderButton}
                        >
                            <FiBarChart2 className="chip-icon" />
                            Build Report
                        </button>
                    )}
                </div>

                <div className="ai-disclaimer" role="note">
                    <div className="ai-disclaimer-text">Responses are generated by AI and may contain mistakes. Please verify important details.</div>
                </div>
            </div>
        </form>
    );
};
export default MessageInput;
